import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Card, CardHeader } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { useShell } from "@/components/Shell";

/**
 * Compact, read-only snapshot of bot configuration. Each row links to the
 * relevant settings sub-page. Empty values display as "Not set" — never a
 * blank cell.
 */
export function BotConfigCard() {
  const { home, loading } = useShell();

  if (loading && !home) return <BotConfigSkeleton />;
  if (!home) return null;

  const cg = home.campground;
  const wc = cg.widget_config ?? {};

  const personality = derivePersonality(cg.system_prompt);
  const domains = cg.allowed_domains ?? [];

  return (
    <Card>
      <CardHeader
        title="Bot configuration"
        right={
          <Link
            to="/settings/bot"
            className="inline-flex items-center gap-1 text-xs text-coral hover:text-coral-hover"
          >
            Edit <ArrowRight size={12} strokeWidth={1.75} />
          </Link>
        }
      />
      <dl className="grid grid-cols-1 gap-px bg-line border-t border-line sm:grid-cols-2">
        <Row label="Personality" value={personality} />
        <Row label="Welcome message" value={wc.welcome_message || "Not set"} />
        <Row
          label="Allowed domains"
          value={
            domains.length === 0 ? (
              <span className="text-warning">No domains — widget will be blocked everywhere</span>
            ) : (
              <span className="break-all">{domains.join(", ")}</span>
            )
          }
        />
        <Row
          label="Widget appearance"
          value={
            <div className="flex flex-wrap items-center gap-2">
              {wc.color_primary && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-3.5 w-3.5 rounded-sm border border-line"
                    style={{ backgroundColor: wc.color_primary }}
                  />
                  <code className="rounded bg-surface-3 px-1 py-0.5 text-2xs text-text-strong">
                    {wc.color_primary}
                  </code>
                </span>
              )}
              <span>·</span>
              <span>{wc.position ?? "right"}</span>
            </div>
          }
        />
      </dl>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-surface px-5 py-3.5">
      <dt className="text-2xs uppercase tracking-[0.12em] text-text-faint">{label}</dt>
      <dd className="mt-1 text-sm text-text">{value}</dd>
    </div>
  );
}

function derivePersonality(prompt: string | null): string {
  if (!prompt) return "Default (friendly)";
  // Heuristic preview until we wire personality presets into the bot
  // settings page. Surfaces a snippet so the owner can recognize their
  // own config without rendering an inline 800-char prompt.
  const t = prompt.toLowerCase();
  if (t.includes("friendly") || t.includes("warm")) return "Custom (friendly tone)";
  if (t.includes("professional") || t.includes("formal")) return "Custom (professional tone)";
  if (t.includes("concise") || t.includes("brief")) return "Custom (concise tone)";
  return "Custom prompt";
}

function BotConfigSkeleton() {
  return (
    <Card>
      <CardHeader title="Bot configuration" />
      <div className="grid grid-cols-1 gap-px bg-line border-t border-line sm:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-surface px-5 py-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-2 h-4 w-3/4" />
          </div>
        ))}
      </div>
    </Card>
  );
}
